import { jsx as _jsx } from "react/jsx-runtime";
import React, { useContext, useMemo, useCallback } from 'react';
import { useSnackbar as useNotistack } from 'notistack';
import { getErrorDetails } from '../../utils/getErrorDetails';
import { SnackbarContent } from './SnackbarContent';
import { SnackbarErrorContent } from './SnackbarErrorContent';
export const SnackbarContext = /*#__PURE__*/ React.createContext(undefined);
// Helper to create a variant-specific enqueue function
function useEnqueueFunction(enqueueSnackbar, severity) {
    return useCallback((message, options)=>{
        return enqueueSnackbar(message, {
            preventDuplicate: options?.preventDuplicate ?? true,
            content: (_, message)=>/*#__PURE__*/ _jsx(SnackbarContent, {
                    severity: severity,
                    message: message,
                    action: options?.action
                })
        });
    }, [
        enqueueSnackbar,
        severity
    ]);
}
const ErrorToAlertSeverity = {
    error: 'error',
    info: 'info',
    warning: 'warning'
};
/**
 * Hook that provides snackbar methods. Must be used within a SnackbarProvider.
 * This hook should only be used internally by SnackbarProviderInner.
 * For external use, use the context from SnackbarProvider.
 */ export function useSnackbarInternal() {
    const { enqueueSnackbar, closeSnackbar } = useNotistack();
    const successSnackbar = useEnqueueFunction(enqueueSnackbar, 'success');
    const infoSnackbar = useEnqueueFunction(enqueueSnackbar, 'info');
    const warningSnackbar = useEnqueueFunction(enqueueSnackbar, 'warning');
    const errorSnackbar = useEnqueueFunction(enqueueSnackbar, 'error');
    const exceptionSnackbar = useCallback((error, message, options)=>{
        const errorDetails = getErrorDetails(error, typeof message === 'string' ? {
            severity: 'error',
            title: message
        } : message);
        return enqueueSnackbar(errorDetails.title, {
            persist: true,
            preventDuplicate: options?.preventDuplicate ?? true,
            content: (key)=>/*#__PURE__*/ _jsx(SnackbarErrorContent, {
                    id: key,
                    severity: ErrorToAlertSeverity[errorDetails.severity],
                    title: errorDetails.title,
                    subtitle: errorDetails.message,
                    details: errorDetails.serverMessages,
                    closeSnackbar: closeSnackbar
                })
        });
    }, [
        enqueueSnackbar,
        closeSnackbar
    ]);
    const context = useMemo(()=>({
            closeSnackbar,
            errorSnackbar,
            exceptionSnackbar,
            infoSnackbar,
            successSnackbar,
            warningSnackbar
        }), [
        closeSnackbar,
        errorSnackbar,
        exceptionSnackbar,
        infoSnackbar,
        successSnackbar,
        warningSnackbar
    ]);
    return context;
}
/**
 * Gets the SnackbarContext and throws if the provider is missing.
 */ export function useSnackbar() {
    const ctx = useContext(SnackbarContext);
    if (ctx === undefined) {
        throw new Error('No SnackbarContext found. Did you forget the SnackbarProvider?');
    }
    return ctx;
}

//# sourceMappingURL=useSnackbar.js.map